import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronDown, Plus, ArrowRight } from "lucide-react";
import DashboardShell from "@/components/DashboardShell";
import MoonPhase from "@/components/MoonPhase";
import api from "@/lib/api";

function timeAgo(iso) {
  if (!iso) return "—";
  const mins = Math.floor((Date.now() - new Date(iso).getTime()) / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

export default function Subjects() {
  const [subjects, setSubjects] = useState([]);
  const [open, setOpen] = useState(null);

  useEffect(() => {
    api.get("/subjects").then(({ data }) => setSubjects(data.subjects || [])).catch(() => {});
  }, []);

  return (
    <DashboardShell>
      <div className="mb-8 flex items-end justify-between">
        <div>
          <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-zinc-400">My Subjects</span>
          <h1 className="mt-3 font-sans text-3xl font-light tracking-tight md:text-4xl">Your constellation of topics.</h1>
        </div>
        <Link to="/summarize" data-testid="new-summary-cta" className="shimmer hidden items-center gap-2 rounded-full bg-white px-5 py-2.5 text-sm font-semibold text-[#06080d] sm:flex">
          <Plus className="h-4 w-4" /> New summary
        </Link>
      </div>

      {subjects.length === 0 ? (
        <div className="glass flex flex-col items-center gap-3 rounded-2xl p-12 text-center" data-testid="subjects-empty">
          <p className="text-zinc-400">No subjects yet. Create a summary and we'll group it here.</p>
          <Link to="/summarize" className="shimmer rounded-full bg-white px-5 py-2 text-sm font-semibold text-[#06080d]">Create a summary</Link>
        </div>
      ) : (
        <div className="grid gap-4 md:grid-cols-2">
          {subjects.map((s) => (
            <div key={s.subject} className="glass rounded-2xl p-6" data-testid={`subject-card-${s.subject}`}>
              <button onClick={() => setOpen(open === s.subject ? null : s.subject)} className="flex w-full items-center justify-between text-left" data-testid={`subject-toggle-${s.subject}`}>
                <div className="flex items-center gap-3">
                  <MoonPhase accuracy={s.accuracy} size={28} />
                  <div>
                    <p className="font-sans text-lg font-medium text-white">{s.subject}</p>
                    <p className="font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500">
                      {s.summaries} summaries · {s.quiz_questions} questions · {timeAgo(s.last_studied)}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <span className="font-sans text-lg font-light text-white">
                    {s.accuracy != null ? `${s.accuracy}%` : <span className="text-xs text-zinc-500">Not tested</span>}
                  </span>
                  <ChevronDown className={`h-4 w-4 text-zinc-400 transition-transform ${open === s.subject ? "rotate-180" : ""}`} />
                </div>
              </button>

              {open === s.subject && (
                <div className="mt-5 space-y-2 border-t border-white/10 pt-5" data-testid={`subject-summaries-${s.subject}`}>
                  {s.summary_list.map((sum) => (
                    <Link
                      key={sum.summary_id}
                      to={`/summary/${sum.summary_id}`}
                      data-testid={`subject-summary-${sum.summary_id}`}
                      className="flex items-center justify-between rounded-xl border border-white/10 px-4 py-3 transition-colors hover:border-white/25"
                    >
                      <div>
                        <p className="text-sm font-medium text-white">{sum.title}</p>
                        <p className="font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500">{sum.source} · {sum.length}</p>
                      </div>
                      <ArrowRight className="h-4 w-4 text-zinc-500" />
                    </Link>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </DashboardShell>
  );
}
